/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import AdminLinkTabs from '../../components/AdminLinkTabs';
import AdminTable from '../../components/AdminTable';
import DashboardHeader from '../../components/DashboardHeader';
import EmptyRecord from '../../components/EmptyRecord';
import RolesAndPermissionGuard from '../../components/RolesAndPermissionGuard';
import { useGlobalState } from '../../contexts';
import useFilterMethods from '../../customHooks/useFilterMethods';
import useGetQueryParams from '../../customHooks/useGetQueryParams';
import useTableColumn from '../../customHooks/useTableColumn';
import useToast from '../../customHooks/useToast';
import { GET_BILLS_PAYMENT } from '../../graphql/admin_queries';
import useCustomQuery from '../../network/useCustomQuery';
import EmptyMerchantSettlement from '../../svgs/EmptyMerchantSettlement';
import generateCSV from '../../utils/generateCSV';
import generatePDF from '../../utils/generatePdf';
import { adminHasPermission, cleanUpBillsPayment } from '../../utils/helpers';
import CustomerTabs from '../AdminCustomers/CustomerTabs';
import { AdminRouteName } from '../CONSTANTS';
import PermissionDenied from '../PermissionDenied';

const limit = 14;
interface IMerchantTableRecord{
defaultUrl?:string;
toggleStorageId?:string;
tabStatus?:string;
hasNameAndImage?:boolean;
recordPermission:string;
payload?: any;
userId?: string;
noSubTab?: boolean
}
const BillsPaymentTableRecords = ({
  defaultUrl, toggleStorageId, tabStatus, hasNameAndImage, recordPermission, payload, noSubTab,
}:IMerchantTableRecord) => {
  const { profile } = useGlobalState();
  const adminPermissions = profile?.role?.permissions || [];
  const Toast = useToast();
  const [getBillsPayment, billsLoading, billsPayment] = useCustomQuery({
    query: GET_BILLS_PAYMENT,
    onError: () => {},
    onData: () => {},
  });
  const { getQuery, getFilterQueries } = useGetQueryParams();
  const search = getQuery('search') || '';
  const page = Number(getQuery('page'));
  const { push } = useHistory();

  const allColumns = [
    ['image', 'email', 'hasBottomValue', 'name'],
    'biller',
    'amount',
    'fees',
    'createdAt',
    'status',
  ];
  const columnLabelFormat = {
    name: 'Customer',
    biller: 'Biller',
    amount: 'Total Amount',
    fees: 'Fees',
    createdAt: 'Date',
  };
  const { getColumns, applyChanges, selectedColumns } = useTableColumn(allColumns, toggleStorageId);

  const [billsPaymentRec, setBillsPaymentRec] = useState(null);
  const setPage = (pageNum) => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('page', pageNum);
    push({
      search: searchParams.toString(),
    });
  };
  if (!page) {
    push(`${defaultUrl}?page=1&search=${search}`);
  }

  const onSearchChange = (e) => {
    push(`${defaultUrl}?page=1&search=${e.target.value}`);
  };

  const TabsRaw = [
    {
      id: 1,
      name: 'All',
      link: AdminRouteName.billsPaymentRoute,
      permission: 'can-get-bills-payment',
      count: billsPaymentRec?.all || 0,
    },
    {
      id: 2,
      name: 'Pending',
      link: AdminRouteName.pendingBillsPaymentRoute,
      permission: 'can-get-bills-payment',
      count: billsPaymentRec?.pending || 0,
    },
    {
      id: 3,
      name: 'Successful',
      link: AdminRouteName.successfulBillsPaymentRoute,
      permission: 'can-get-bills-payment',
      count: billsPaymentRec?.success || 0,
    },
    {
      id: 4,
      name: 'Failed',
      link: AdminRouteName.failedBillsPaymentRoute,
      permission: 'can-get-bills-payment',
      count: billsPaymentRec?.failed || 0,
    },
  ];

  const Tabs = TabsRaw?.map((tab) => ({
    ...tab,
    disabled: !adminHasPermission(adminPermissions, tab.permission),
  }));

  useEffect(() => {
    if (page) {
      const result = getFilterQueries();
      getBillsPayment({
        variables: {
          offset: (page - 1) * limit,
          limit,
          filter: {
            search,
            status: tabStatus,
            minAmount: result.min ? Number(result.min) : 0,
            maxAmount: result.max ? Number(result.max) : 0,
          },
        },
      });
    }
  }, [page, search, tabStatus]);

  const onFilterSubmit = (result) => {
    getBillsPayment({
      variables: {
        offset: (page - 1) * limit,
        limit,
        filter: {
          startDate: result.from,
          endDate: result.to,
          search,
          status: tabStatus,
          minAmount: result.min ? Number(result.min) : 0,
          maxAmount: result.max ? Number(result.max) : 0,
        },
      },
    });
  };

  const {
    filterObj,
  } = useFilterMethods({
    allowedFilters: ['dateRange', 'amountRange'],
    defaultUrl,
    cb: onFilterSubmit,
    Toast,
    customFilterInformation: {
      dateRange: {
        label: 'Date',
      },
      amountRange: {
        label: 'Total Amount',
      },

    },
  });

  useEffect(() => {
    if (billsPayment?.getBillPaymentsAdminCa?.billPayments) {
      setBillsPaymentRec(billsPayment?.getBillPaymentsAdminCa);
    }
  }, [billsPayment]);

  const [getExportRecord] = useCustomQuery({
    query: GET_BILLS_PAYMENT,
    onError: () => {},
    onData: () => {},
  });

  const handleExport = async (type: string) => {
    try {
      const result = getFilterQueries();
      const output = await getExportRecord({
        variables: {
          offset: (page - 1) * limit,
          limit,
          // ...(tabStatus ? { statusFilter: tabStatus } : {}),
          filter: {
            startDate: result.from,
            endDate: result.to,
            search,
            status: tabStatus,
            minAmount: result.min ? Number(result.min) : 0,
            maxAmount: result.max ? Number(result.max) : 0,
          },
        },
      });

      if (output?.data?.getBillPaymentsAdminCa
        ?.billPayments) {
        const rec = cleanUpBillsPayment(output?.data?.getBillPaymentsAdminCa
          ?.billPayments?.data);
        const func = type === 'pdf' ? generatePDF : generateCSV;
        const selectedHeaders = [
          'S/N',
          // change image__email__hasBottomValue__name concated columns to a single column of name
          ...(getColumns?.()?.map((column) => (column === 'image__email__hasBottomValue__name' ? ['name', 'email'] : column)) || []),
        ];

        func({
          payload: rec,
          title: 'Bills Payment',
          selectedHeaders,
          formatHeaders: columnLabelFormat,
          cb: () => Toast.success('Exported successfully'),
        });
      }
    } catch (error) {
      Toast.error('Error while exporting');
    }
  };

  return (
    <div>
      <DashboardHeader
        title="Bill Payments"
        smallSubTitle={`(${billsPaymentRec?.billPayments?.count || 0})`}
        searchPlaceholder="Customer name / Email address"
        search={search}
        onSearchChange={onSearchChange}
        searchLoading={billsLoading}
        filterObj={filterObj}
        columnObject={{
          allColumns,
          formatLabels: columnLabelFormat,
          applyChanges,
          selectedColumns,
        }}
        exportObj={{
          handleExport,
        }}
        // disableExport={!adminHasPermission(adminPermissions, 'can-export-transaction-records')}
        // disableSearch={!adminHasPermission(adminPermissions, 'can-search-transaction-records')}
        // disbleFilter={!adminHasPermission(adminPermissions, 'can-filter-transaction-records')}
        payload={payload}
      >
        {!noSubTab && (

        <AdminLinkTabs>
          <CustomerTabs Tabs={Tabs} />
        </AdminLinkTabs>
        )}
      </DashboardHeader>

      <RolesAndPermissionGuard permissionName={recordPermission} hasErrorTemplate={<PermissionDenied height="60vh" />}>
        <div className="transaction-table-details-cover">
          {billsPaymentRec?.billPayments?.count ? (
            <AdminTable
              refreshing={billsLoading}
              page={page}
              setPage={setPage}
              pageSize={limit}
              minHeight="calc(100vh - 280px)"
              count={billsPaymentRec?.billPayments?.count}
              contents={cleanUpBillsPayment(billsPaymentRec?.billPayments?.data)}
              reference="id"
              hideDominantImage
              clickable
              hasNameAndImage={hasNameAndImage}
              handleClick={(data) => push(`/bills-payment/bills-payment-details/${data?.id}`)}
              allowKeyArr={getColumns(true)}
              mobileAllowKeyArr={
                [
                  'status',
                  ['merchant', 'txRef'],
                  ['email', 'customer'],
                ]
              }
              formatLabels={columnLabelFormat}
            />
          ) : (
            <EmptyRecord
              height="calc(100vh - 240px)"
              title={billsLoading ? 'Fetching bills payment...' : 'No bills payment yet'}
              main="All bills payment from customers on your Payfi will appear here"
              // footer="Receive Payments"
              loading={billsLoading}
              Icon={EmptyMerchantSettlement}
            />
          )}

        </div>
      </RolesAndPermissionGuard>

    </div>
  );
};
export default BillsPaymentTableRecords;
